import type { AnySceneObjectT, TextI, TickerI } from "tweenly"
import { useMemo } from "react"
import { selectUndoable } from "../../../../helpers/selector.helpers"
import useObject from "../../../../hooks/useObject"
import { useAppDispatch, useAppSelector } from "../../../../hooks/useRedux"
import { useTickerTimeline } from "../../../../hooks/useTickerTimeline"
import {
    addTextCrawlContinuousAction,
    addTextCrawlStaggeredAction,
    addTextScrollDownAction,
    addTextScrollUpAction,
    addTextScrollUpDownAction,
    deleteTickerTextAction,
    selectObjectsByIds,
    updateCrawlPropertyAction,
    updateScrollPropertyAction,
    updateTextCrawlActionAsync,
    updateTickerObjectWithoutRestartAction,
    updateTickerStyleWithoutRestartAction,
    updateTickerTextsAttributeAction,
    updateTickerTextsPropertyAction,
    updateTickerTextsStyleWithCopiesAction,
    updObjectAction,
} from "../../../../store/slices/objects.slice"
import Fill from "../ExpandableSections/Fill"
import Appearance from "../ExpandableSections/Appearance"
import SizeAndPosition from "../ExpandableSections/SizeAndPosition"
import CrawlTools from "./CrawlTools"
import ScrollTools from "./ScrollTools"
import TickerFont from "./TickerFont"
import TickerText from "./TickerText"

interface Props {
    object: TickerI
    handleSetStyleValue: (property: any, value: any) => void
    handleSetObjectValue: (property: any, value: any) => void
    handleSetAttributeValue: (property: any, value: any) => void
}

const TickerTools = ({
    object,
    handleSetStyleValue,
    handleSetObjectValue,
    handleSetAttributeValue,
}: Props) => {
    const dispatch = useAppDispatch()

    // Vlastnosti, které NEOVLIVŇUJÍ geometrii tickeru a nemají způsobit restart animace
    const isNonGeometryProperty = (property: string) => {
        const nonGeometryProperties = [
            "backgroundColor",
            "color",
            "opacity",
            "borderTopLeftRadius",
            "borderTopRightRadius",
            "borderBottomLeftRadius",
            "borderBottomRightRadius",
            "borderRadius",
            "borderColor",
            "borderWidth",
            "top",
            "left",
            "position",
            "overflow",
            "alignItems",
            "justifyContent",
        ]
        return nonGeometryProperties.includes(property)
    }

    // find every Texts Object in structure
    const { findObjectById } = useObject()
    const objectsState = useAppSelector((state) => selectUndoable(state).objects)
    const subTimelines = useAppSelector((state) => state.subTimelines.value)

    const maskObject = useMemo(() => {
        return findObjectById(object.childIds[0]) as TickerI
    }, [findObjectById, object.childIds])

    const textObjects: TextI[] = useMemo(() => {
        return maskObject && maskObject.childIds
            ? (selectObjectsByIds(objectsState, maskObject?.childIds) as TextI[])
            : []
    }, [maskObject, objectsState])

    const actualTimeline = useMemo(() => {
        if (maskObject) return subTimelines[maskObject.id]
    }, [maskObject, subTimelines])

    const { playTimeline, pauseTimeline } = useTickerTimeline(object, textObjects, actualTimeline)

    const handleAddCrawlTextTicker = () => {
        if (maskObject.tickerType === "crawl-continuous") {
            dispatch(addTextCrawlContinuousAction({ maskObject }))
        } else if (maskObject.tickerType === "crawl-staggered") {
            dispatch(addTextCrawlStaggeredAction({ maskObject }))
        }
    }
    const handleAddScrollTextTicker = () => {
        actualTimeline?.clear()
        if (maskObject.tickerType === "scroll-up-down") {
            dispatch(addTextScrollUpDownAction({ maskObject }))
        } else if (maskObject.tickerType === "scroll-up") {
            dispatch(addTextScrollUpAction({ maskObject }))
        } else if (maskObject.tickerType === "scroll-down") {
            dispatch(addTextScrollDownAction({ maskObject }))
        }
    }

    const handleDeleteText = (deleteObject: AnySceneObjectT) => {
        dispatch(
            deleteTickerTextAction({
                object: object,
                maskObject: maskObject,
                textObject: deleteObject as TextI,
                textObjects: textObjects,
            })
        )
    }

    const handleUpdateText = (objectId: string, property: string, value: string) => {
        if (object.tickerType.startsWith("scroll")) {
            dispatch(updObjectAction({ id: objectId, property: property, value: value }))
        } else {
            dispatch(
                updateTextCrawlActionAsync(
                    object,
                    maskObject,
                    textObjects,
                    objectId,
                    property,
                    value
                )
            )
        }
    }

    const handleSetTickerStyleValue = (property: any, value: any) => {
        // Vlastnosti vyžadující přepočet geometrie a pozic textů
        const geometryAffectingProperties = ["width", "height"]
        const requiresGeometryRecalculation = geometryAffectingProperties.includes(property)

        if (requiresGeometryRecalculation) {
            // Pro vlastnosti ovlivňující geometrii: vyčistit timeline a přepočítat pozice
            actualTimeline?.clear()

            if (object.tickerType.startsWith("scroll")) {
                dispatch(
                    updateScrollPropertyAction({ object, maskObject, textObjects, property, value })
                )
            } else {
                dispatch(
                    updateCrawlPropertyAction({ object, maskObject, textObjects, property, value })
                )
            }
        } else {
            // Pro ostatní vlastnosti (top, left, position, overflow, atd.):
            // použít speciální akci bez přerušení animace
            dispatch(
                updateTickerStyleWithoutRestartAction({
                    id: object.id,
                    property,
                    value,
                })
            )
        }
    }
    const handleSetTextsStyleValue = (property: any, value: any) => {
        // Vlastnosti textů, které OVLIVŇUJÍ geometrii (šířku textu)
        // Pro crawl tickery tyto změny by měly ideálně přepočítat pozice, ale to je složitější
        // Pro scroll tickery tyto změny neovlivňují pozice
        const textGeometryAffectingProperties = [
            "fontSize",
            "fontFamily",
            "fontWeight",
            "textTransform",
        ]
        const affectsGeometry = textGeometryAffectingProperties.includes(property)

        if (affectsGeometry && object.tickerType.startsWith("crawl")) {
            // Pro crawl tickery při změně fontu bychom měli přepočítat pozice
            // Použijeme původní akci, která nastavuje updatedAt
            dispatch(
                updateTickerTextsPropertyAction({
                    object,
                    maskObject,
                    textObjects,
                    property,
                    value,
                })
            )
        } else {
            // Pro ostatní vlastnosti (color, opacity, textAlign) nebo pro scroll tickery:
            // použít speciální akci bez přerušení animace
            // Tato akce také aktualizuje DOM kopie pro continuous crawl ticker
            dispatch(
                updateTickerTextsStyleWithCopiesAction({
                    object,
                    maskObject,
                    textObjects,
                    property,
                    value,
                })
            )
        }
    }
    const handleSetIsEditable = (property: any, value: any) => {
        dispatch(updateTickerTextsAttributeAction({ textObjects, property, value }))
    }

    // Wrapper pro změny stylů, který rozlišuje, zda vlastnost ovlivňuje geometrii
    const handleSetTickerStyleValueSafe = (property: any, value: any) => {
        if (isNonGeometryProperty(property)) {
            // Pro vlastnosti neovlivňující geometrii: použít speciální akci bez restartu timeline
            dispatch(
                updateTickerStyleWithoutRestartAction({
                    id: object.id,
                    property,
                    value,
                })
            )
        } else {
            // Pro ostatní vlastnosti: použít původní handler
            handleSetStyleValue(property, value)
        }
    }

    // Wrapper pro změny objektových vlastností (fill, atd.) bez restartu timeline
    // Změny fill (barva), opacity atd. neovlivňují geometrii tickeru
    const handleSetTickerObjectValueSafe = (property: any, value: any) => {
        dispatch(
            updateTickerObjectWithoutRestartAction({
                id: object.id,
                property,
                value,
            })
        )
    }

    return (
        <div>
            {object.tickerType.startsWith("crawl") ? (
                <CrawlTools
                    object={object}
                    maskObject={maskObject}
                    textObjects={textObjects}
                    playTimeline={playTimeline}
                    pauseTimeline={pauseTimeline}
                />
            ) : (
                <ScrollTools
                    object={object}
                    maskObject={maskObject}
                    textObjects={textObjects}
                    playTimeline={playTimeline}
                    pauseTimeline={pauseTimeline}
                />
            )}
            <TickerText
                textObjects={textObjects}
                handleUpdateText={handleUpdateText}
                handleDeleteText={handleDeleteText}
                handleAddTextTicker={
                    object.tickerType.startsWith("crawl")
                        ? handleAddCrawlTextTicker
                        : handleAddScrollTextTicker
                }
                handleSetIsEditable={handleSetIsEditable}
            />
            <SizeAndPosition object={object} handleSetValue={handleSetTickerStyleValue} />
            <Appearance object={object} handleSetValue={handleSetTickerStyleValueSafe} />
            <Fill
                expanded={false}
                object={object}
                handleSetStyleValue={handleSetTickerStyleValueSafe}
                handleSetObjectValue={handleSetTickerObjectValueSafe}
                handleSetAttributeValue={handleSetAttributeValue}
            />
            {textObjects.length > 0 && (
                <TickerFont
                    tickerType={object.tickerType.startsWith("crawl") ? "crawl" : "scroll"}
                    textObjects={textObjects}
                    handleSetStyleValue={handleSetTextsStyleValue}
                />
            )}
        </div>
    )
}

export default TickerTools
